import { useMemo, useState } from "react";
import {
  blankPlanDraft,
  countByDay,
  formatLocalYmd,
  missedPlanIds,
  planToPlanSeed,
  sessionsByDay,
  type Plan,
} from "@heraion/shared";

import { usePlans } from "../Plans/usePlans";
import type { WorkoutsDiaryOptions } from "./useWorkoutsDiary";

type CalendarOptions = Pick<
  WorkoutsDiaryOptions,
  "items" | "allLoaded" | "unit" | "apiFetch" | "durableWrite" | "onPlanSeed" | "sub"
> & {
  /** The calendar is in front: the plans are read. */
  enabled: boolean;
};

// #368, #374: what the diary's calendar shows. The planned days come from the
// plans, the done ones from the sessions the list already loaded (no read of
// its own). Days are local, never the UTC day of `startedAt`.
// A plan of a past day not done (missedPlanIds) says so, and can be planned
// again. Not a plan the reader wrote for other athletes (#499).
export function useDiaryCalendar(o: CalendarOptions) {
  const today = formatLocalYmd(new Date());
  const [day, setDay] = useState(today);
  const { plans, status } = usePlans({
    apiFetch: o.apiFetch,
    durableWrite: o.durableWrite,
    enabled: o.enabled,
  });
  const items = o.items;
  const markers = useMemo(() => countByDay(plans.map((p) => p.scheduledFor)), [plans]);
  const byDay = useMemo(() => sessionsByDay(items ?? []), [items]);
  const done = useMemo(
    () => Object.fromEntries(Object.entries(byDay).map(([d, list]) => [d, list.length])),
    [byDay],
  );
  const dayPlans = useMemo(() => plans.filter((p) => p.scheduledFor === day), [plans, day]);
  const allLoaded = items !== null && o.allLoaded;
  const sub = o.sub;
  const missed = useMemo(
    () => missedPlanIds(plans, items ?? [], today, allLoaded, sub),
    [plans, items, today, allLoaded, sub],
  );
  const missedDays = useMemo(
    () => new Set(plans.flatMap((p) => (missed.has(p.id) && p.scheduledFor ? [p.scheduledFor] : []))),
    [plans, missed],
  );

  const unit = o.unit;
  // The + plans on the day: today and later only.
  const onPlanOn =
    day >= today
      ? () => {
          if (unit) o.onPlanSeed({ draft: blankPlanDraft(), unit, scheduledFor: day });
        }
      : undefined;
  const onPlanAgain = (plan: Plan) => {
    if (unit) o.onPlanSeed(planToPlanSeed(plan, unit));
  };

  return {
    day,
    setDay,
    status,
    markers,
    done,
    dayPlans,
    daySessions: byDay[day] ?? [],
    missed,
    missedDays,
    /** false: the unit is not known yet, nothing can be planned. */
    ready: unit !== null,
    onPlanOn,
    onPlanAgain,
  };
}
